import { capitalize } from "@mui/material";
import { BubbleText } from "@totalenergiescode/mobility-business-rev-design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetUserDetail } from "@/api/graphql/accounts/getUser/hooks.ts";
import { useGetNotificationsByUser } from "@/api/graphql/notifications/user/hooks.ts";
import { StyledCellText } from "@/components/Agents/List/styles";
import { NOTIFICATION_PER_PAGE } from "@/components/Notifications/constants.ts";
import {
  getCompanyName,
  getStatusAndColor,
} from "@/components/Notifications/userList/helper.ts";
import { StyledNoDataText } from "@/components/Notifications/userList/styles.ts";
import { useFormattedDate } from "@/hooks/i18n/useFormatDate/hook";
export const useNotificationTableData = (userId: string) => {
  const { t: tCompanies } = useTranslation("companies");
  const { t: tNotifications } = useTranslation("notifications");
  const formattedDate = useFormattedDate();
  const {
    data,
    isLoading: isNotificationsLoading,
    totalNotifications,
    hasNextPage,
    fetchNextPage,
  } = useGetNotificationsByUser({ pageSize: NOTIFICATION_PER_PAGE, userId });
  const { userData: user, isLoading: isUserLoading } = useGetUserDetail(userId);
  const notifications = useMemo(
    () => data?.pages?.flatMap((current) => current?.data ?? []) ?? [],
    [data],
  );
  const columns = useMemo(
    () => [
      {
        columnName: tCompanies("companies.list.title"),
        displayedByDefault: true,
      },
      {
        columnName: tNotifications("notifications.table.object"),
        displayedByDefault: true,
      },
      {
        columnName: tNotifications("notifications.table.attachment"),
        displayedByDefault: true,
      },
      {
        columnName: tNotifications("notifications.table.status"),
        displayedByDefault: true,
      },
      {
        columnName: tNotifications("notifications.table.dateSent"),
        displayedByDefault: true,
      },
    ],
    [tCompanies, tNotifications],
  );
  const rows = useMemo(
    () =>
      notifications.map((notification) => {
        const { translatedStatus, color } = getStatusAndColor({
          status: notification.status,
          failedReason: notification.failedReason,
          tNotifications,
        });
        const getCompanyNameCall = getCompanyName({
          code: notification.accountNumber,
          name: notification.accountName,
          tNotifications,
        });
        const companyName =
          notification.accountNumber === "" &&
          notification.accountName === "" ? (
            <StyledNoDataText variant={"text1"}>
              {getCompanyNameCall}
            </StyledNoDataText>
          ) : (
            getCompanyNameCall
          );

        return {
          data: {
            account: companyName,
            subject: notification.subject,
            attachments: notification.hasAttachments
              ? tNotifications("notifications.attachments.with")
              : tNotifications("notifications.attachments.without"),
            status: (
              <StyledCellText variant='text2'>
                <BubbleText
                  text={capitalize(translatedStatus)}
                  variant={color}
                />
              </StyledCellText>
            ),
            dateSent: formattedDate(notification.createdTs),
          },
          key: notification.id,
        };
      }),
    [notifications, formattedDate, tNotifications],
  );

  return {
    columns,
    rows,
    totalNotifications,
    isUserLoading,
    isNotificationsLoading,
    user,
    hasNextPage,
    fetchNextPage,
  };
};
export const useBreadCrumbItems = (fullname: string) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tNotifications } = useTranslation("notifications");

  return [
    {
      title: tCommon("common.features.dashboard"),
      path: "/" as const,
    },
    {
      title: fullname || "",
      path: "/users/$id" as const,
    },
    {
      title: tNotifications("notifications.title"),
      path: undefined,
    },
  ];
};
